import React, { useState, useEffect } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  CircularProgress,
  Alert,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Tabs,
  Tab,
  Divider,
  Snackbar
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Visibility as VisibilityIcon,
  Image as ImageIcon,
  AccessTime as AccessTimeIcon
} from '@mui/icons-material';
import MuiAlert from '@mui/material/Alert';
import axios from 'axios';
import { useAuthStore } from '../../store/authStore';

// Property type options
const propertyTypes = [
  'Коттедж',
  'Баня',
  'Дача',
  'Апартаменты',
  'Гостевой дом',
  'Загородный дом'
];

// Rental type options
const rentalTypes = [
  { value: 'hourly', label: 'Почасовая' },
  { value: 'daily', label: 'Посуточная' }
];

// Property management component
const PropertyManagement = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  
  const navigate = useNavigate();
  const { user } = useAuthStore();
  
  // Table state
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(isMobile ? 5 : 10);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Delete dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [propertyToDelete, setPropertyToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  
  // Snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Fetch properties from API
  useEffect(() => {
    fetchProperties();
  }, []);

  const fetchProperties = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const token = localStorage.getItem('token');
      const config = {
        headers: {
          Authorization: `Bearer ${token}`
        }
      };
      
      const { data } = await axios.get('/api/properties/admin', config);
      setProperties(data);
    } catch (error) {
      console.error('Error fetching properties:', error);
      setError('Не удалось загрузить объекты недвижимости');
    } finally {
      setLoading(false);
    }
  };

  // Handle search
  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
    setPage(0);
  };

  // Filter properties based on search query
  const filteredProperties = properties.filter(property => {
    const searchLower = searchQuery.toLowerCase();
    return (
      property.name.toLowerCase().includes(searchLower) ||
      property.address.toLowerCase().includes(searchLower)
    );
  });

  // Pagination handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Delete property handlers
  const handleOpenDeleteDialog = (property) => {
    setPropertyToDelete(property);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setPropertyToDelete(null);
  };

  const handleDeleteProperty = async () => {
    if (!propertyToDelete) return;
    
    try {
      setDeleteLoading(true);
      
      const token = localStorage.getItem('token');
      await axios.delete(`/api/properties/${propertyToDelete.id}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      setProperties(properties.filter(p => p.id !== propertyToDelete.id));
      setSnackbar({
        open: true,
        message: 'Объект недвижимости успешно удален',
        severity: 'success'
      });
      handleCloseDeleteDialog();
    } catch (error) {
      console.error('Error deleting property:', error);
      setSnackbar({
        open: true,
        message: 'Не удалось удалить объект недвижимости',
        severity: 'error'
      });
    } finally {
      setDeleteLoading(false);
    }
  };

  // Snackbar close handler
  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Format price display
  const formatPrice = (property) => {
    if (property.rentalType === 'hourly' && property.pricePerHour) {
      return `${property.pricePerHour} ₽/час`;
    } else if (property.rentalType === 'daily' && property.pricePerDay) {
      return `${property.pricePerDay} ₽/сутки`;
    }
    return 'Не указана';
  };

  // Format rental type display
  const formatRentalType = (type) => {
    if (type === 'hourly') return 'Почасовая';
    if (type === 'daily') return 'Посуточная';
    return type;
  };

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 } }}>
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between', 
        alignItems: { xs: 'flex-start', sm: 'center' }, 
        gap: { xs: 2, sm: 0 },
        mb: { xs: 2, sm: 3 } 
      }}>
        <Typography 
          variant="h4" 
          component="h1"
          sx={{ 
            fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' },
            fontWeight: 'bold'
          }}
        >
          Управление объектами
        </Typography>
        <Button
          variant="contained"
          color="primary"
          size={isMobile ? "small" : "medium"}
          startIcon={<AddIcon />}
          component={RouterLink}
          to="/admin/properties/new"
        >
          {isMobile ? "Добавить" : "Добавить объект"}
        </Button>
      </Box>

      <Paper sx={{ 
        mb: { xs: 2, sm: 3 }, 
        p: { xs: 1.5, sm: 2 },
        borderRadius: { xs: 1, sm: 2 }
      }}>
        <TextField
          fullWidth
          placeholder="Поиск по названию, адресу или типу..."
          variant="outlined"
          size={isMobile ? "small" : "medium"}
          value={searchQuery}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            )
          }}
        />
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: { xs: 2, sm: 3 } }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <TableContainer component={Paper} sx={{ 
            mb: { xs: 2, sm: 3 },
            borderRadius: { xs: 1, sm: 2 },
            overflowX: 'auto',
            '&::-webkit-scrollbar': {
              height: 8
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: theme.palette.primary.light,
              borderRadius: 4
            }
          }}>
            <Table size={isMobile ? "small" : "medium"}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ p: { xs: 1, sm: 2 } }}>Название</TableCell>
                  <TableCell sx={{ p: { xs: 1, sm: 2 }, display: { xs: 'none', md: 'table-cell' } }}>Адрес</TableCell>
                  <TableCell sx={{ p: { xs: 1, sm: 2 }, display: { xs: 'none', sm: 'table-cell' } }}>Тип аренды</TableCell>
                  <TableCell sx={{ p: { xs: 1, sm: 2 } }}>Цена</TableCell>
                  <TableCell sx={{ p: { xs: 1, sm: 2 }, display: { xs: 'none', md: 'table-cell' } }}>Вместимость</TableCell>
                  <TableCell sx={{ p: { xs: 1, sm: 2 } }}>Статус</TableCell>
                  <TableCell align="right" sx={{ p: { xs: 1, sm: 2 } }}>Действия</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredProperties.length > 0 ? (
                  filteredProperties
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((property) => (
                      <TableRow key={property.id}>
                        <TableCell sx={{ p: { xs: 1, sm: 2 } }}>
                          <Typography variant="body2" noWrap sx={{ maxWidth: { xs: 100, sm: 200 } }}>
                            {property.name}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ p: { xs: 1, sm: 2 }, display: { xs: 'none', md: 'table-cell' } }}>
                          <Typography variant="body2" noWrap sx={{ maxWidth: { sm: 150, md: 200 } }}>
                            {property.address}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ p: { xs: 1, sm: 2 }, display: { xs: 'none', sm: 'table-cell' } }}>
                          {formatRentalType(property.rentalType)}
                        </TableCell>
                        <TableCell sx={{ p: { xs: 1, sm: 2 } }}>{formatPrice(property)}</TableCell>
                        <TableCell sx={{ p: { xs: 1, sm: 2 }, display: { xs: 'none', md: 'table-cell' } }}>
                          {property.capacity || 'Не указана'}
                        </TableCell>
                        <TableCell sx={{ p: { xs: 1, sm: 2 } }}>
                          <Chip
                            label={property.isActive ? 'Активен' : 'Неактивен'}
                            color={property.isActive ? 'success' : 'default'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="right" sx={{ p: { xs: 1, sm: 2 }, whiteSpace: 'nowrap' }}>
                          <IconButton
                            color="primary"
                            component={RouterLink}
                            to={`/properties/${property.id}`}
                            title="Просмотр"
                            size={isMobile ? "small" : "medium"}
                          >
                            <VisibilityIcon fontSize={isMobile ? "small" : "medium"} />
                          </IconButton>
                          <IconButton
                            color="primary"
                            component={RouterLink}
                            to={`/admin/properties/edit/${property.id}`}
                            title="Редактировать"
                            size={isMobile ? "small" : "medium"}
                          >
                            <EditIcon fontSize={isMobile ? "small" : "medium"} />
                          </IconButton>
                          <IconButton
                            color="error"
                            onClick={() => handleOpenDeleteDialog(property)}
                            title="Удалить"
                            size={isMobile ? "small" : "medium"}
                          >
                            <DeleteIcon fontSize={isMobile ? "small" : "medium"} />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                      {searchQuery
                        ? 'Объекты недвижимости не найдены. Попробуйте изменить параметры поиска.'
                        : 'Нет доступных объектов недвижимости.'}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            rowsPerPageOptions={isMobile ? [5, 10] : [5, 10, 25]}
            component="div"
            count={filteredProperties.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage={isMobile ? "Строк:" : "Строк на странице:"}
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} из ${count}`}
            sx={{
              '.MuiTablePagination-selectLabel': {
                margin: { xs: 0, sm: 1 },
              },
              '.MuiTablePagination-displayedRows': {
                margin: { xs: 0, sm: 1 },
              }
            }}
          />
        </>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        fullWidth
        maxWidth="xs"
        fullScreen={isMobile}
      >
        <DialogTitle>Подтверждение удаления</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Вы уверены, что хотите удалить объект "{propertyToDelete?.name}"? Это действие нельзя отменить.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} disabled={deleteLoading}>
            Отмена
          </Button>
          <Button
            onClick={handleDeleteProperty}
            color="error"
            variant="contained"
            disabled={deleteLoading}
            startIcon={deleteLoading && <CircularProgress size={20} />}
          >
            {deleteLoading ? "Удаление..." : "Удалить"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <MuiAlert
          elevation={6}
          variant="filled"
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </MuiAlert>
      </Snackbar>
    </Box>
  );
};

export default PropertyManagement;
